import React, { useState, useEffect, useMemo } from 'react';

const APP_WIDTH = 430; // Match AppShell width

// Pre-generate random positions for floating crystals
const CRYSTAL_POSITIONS = Array.from({ length: 8 }, (_, i) => ({
  width: 30 + ((i * 7) % 40),
  left: (i * 13) % 100,
  top: (i * 17) % 100,
  rotation: (i * 45) % 360,
  duration: 4 + (i % 3),
  delay: (i * 0.25) % 2,
}));

const WalletTransition = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Phase 1: Ice button appears
    const t1 = setTimeout(() => setPhase(1), 100);
    // Phase 2: Text appears
    const t2 = setTimeout(() => setPhase(2), 600);
    // Phase 3: Caution/Pure text
    const t3 = setTimeout(() => setPhase(3), 1200);
    // Phase 4: Progress bar
    const t4 = setTimeout(() => setPhase(4), 1800);
    // Phase 5: Start exit animation at 100%
    const t5 = setTimeout(() => setExiting(true), 3000);
    // Complete after exit animation finishes
    const t6 = setTimeout(() => {
      if (onComplete) onComplete();
    }, 3500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearTimeout(t6);
    };
  }, [onComplete]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: `${APP_WIDTH}px`,
        height: '100%',
        zIndex: 9999,
        background: phase >= 4 ? '#fff' : '#F4D03F',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.8s ease-out, opacity 0.5s ease-out',
        overflow: 'hidden',
        boxShadow: '0 0 60px rgba(0, 0, 0, 0.8)',
        opacity: exiting ? 0 : 1,
      }}
    >
      {/* Hazmat stripe top */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50px',
          background:
            'repeating-linear-gradient(-45deg, #F4D03F 0px, #F4D03F 20px, #000 20px, #000 40px)',
          borderBottom: '3px solid #000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: phase >= 3 ? 1 : 0,
          transform: phase >= 3 ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <span
          style={{
            background: '#000',
            color: '#F4D03F',
            padding: '8px 24px',
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '14px',
            letterSpacing: '6px',
            textTransform: 'uppercase',
            fontWeight: 'bold',
          }}
        >
          ⚠ CAUTION ⚠
        </span>
      </div>

      {/* Hazmat stripe bottom */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50px',
          background:
            'repeating-linear-gradient(-45deg, #F4D03F 0px, #F4D03F 20px, #000 20px, #000 40px)',
          borderTop: '3px solid #000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: phase >= 3 ? 1 : 0,
          transform: phase >= 3 ? 'translateY(0)' : 'translateY(100%)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <span
          style={{
            background: '#000',
            color: '#F4D03F',
            padding: '8px 24px',
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '14px',
            letterSpacing: '6px',
            textTransform: 'uppercase',
            fontWeight: 'bold',
          }}
        >
          99.1% PURE
        </span>
      </div>

      {/* Scanlines */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.05) 0px, rgba(0, 0, 0, 0.05) 1px, transparent 1px, transparent 2px)',
          zIndex: 1,
        }}
      />

      {/* Vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse at center, transparent 40%, rgba(0, 0, 0, 0.5) 100%)',
          zIndex: 2,
        }}
      />

      {/* Main content */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        {/* Ice crystal image */}
        <div
          style={{
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-20deg)',
            transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
            filter:
              phase >= 4
                ? 'drop-shadow(0 0 30px rgba(125, 211, 252, 0.8))'
                : 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))',
          }}
        >
          <img
            src="/images/ice-button.webp"
            alt="Crystal"
            style={{
              width: '200px',
              height: 'auto',
              animation: phase >= 1 ? 'crystalFloat 3s ease-in-out infinite' : 'none',
            }}
          />
        </div>

        {/* Text */}
        <div
          style={{
            textAlign: 'center',
            opacity: phase >= 2 ? 1 : 0,
            transform: phase >= 2 ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.5s ease-out',
          }}
        >
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '36px',
              fontWeight: 'bold',
              color: '#000',
              letterSpacing: '8px',
              margin: 0,
              textTransform: 'uppercase',
              textShadow: phase >= 4 ? '0 0 20px rgba(125, 211, 252, 0.5)' : 'none',
            }}
          >
            TIME TO COOK
          </h1>
          <p
            style={{
              fontFamily: "'Bebas Neue', monospace",
              fontSize: '14px',
              color: phase >= 4 ? '#000' : 'rgba(0, 0, 0, 0.6)',
              letterSpacing: '4px',
              margin: '8px 0 0 0',
              textTransform: 'uppercase',
            }}
          >
            Wallet Connected
          </p>
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: '280px',
            height: '8px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden',
            opacity: phase >= 4 ? 1 : 0,
            transform: phase >= 4 ? 'scaleX(1)' : 'scaleX(0.8)',
            transition: 'all 0.4s ease-out',
          }}
        >
          <div
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #7dd3fc, #0ea5e9, #7dd3fc)',
              backgroundSize: '200% 100%',
              borderRadius: '4px',
              animation:
                phase >= 4
                  ? 'progressFill 1.2s ease-out forwards, shimmer 1s linear infinite'
                  : 'none',
            }}
          />
        </div>

        {/* Chemical formula */}
        <div
          style={{
            fontFamily: "'Bebas Neue', monospace",
            fontSize: '11px',
            color: 'rgba(0, 0, 0, 0.4)',
            letterSpacing: '3px',
            opacity: phase >= 2 ? 1 : 0,
            transition: 'opacity 0.5s ease-out',
          }}
        >
          C₁₀H₁₅N • HEISENBERG GRADE
        </div>
      </div>

      {/* Floating crystals background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        {CRYSTAL_POSITIONS.map((pos, i) => (
          <img
            key={i}
            src="/images/ice-button.webp"
            alt="Crystal animation"
            style={{
              position: 'absolute',
              width: `${pos.width}px`,
              height: 'auto',
              left: `${pos.left}%`,
              top: `${pos.top}%`,
              opacity: 0.15,
              transform: `rotate(${pos.rotation}deg)`,
              animation: `floatCrystal ${pos.duration}s ease-in-out infinite`,
              animationDelay: `${pos.delay}s`,
            }}
          />
        ))}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
        
        @keyframes crystalFloat {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        
        @keyframes floatCrystal {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        
        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
        
        @keyframes shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default WalletTransition;
